/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { canonicalJsonSha256 } from './gtDerivations'
import { type GtDoc, hydrateDoc, type LegacyGtDocument } from './gtModel'

import { isApiError } from '@/api'
import { getGtConflict, getGtRefusal, type GtConflictReason } from '@/api/internal'
import { useSaveAttributionGt } from '@/hooks/useAttributionGt'

const SAVE_DEBOUNCE_MS = 2000
const MIRROR_MAX_AGE_MS = 24 * 60 * 60 * 1000

export type GtSaveStatus = 'saving' | 'saved' | 'unsaved' | 'error' | 'conflict' | 'readonly'

export interface GtConflictInfo {
  currentVersion: number | null
  reason: GtConflictReason
}

export type GtSaveOutcome = 'saved' | 'conflict' | 'error'

export type GtSettleResult = { outcome: 'saved'; version: number } | { outcome: 'conflict' | 'error' }

export interface GtMirror {
  doc: GtDoc
  at: number
}

const mirrorKey = (meetingId: string, version: number) => `gt:${meetingId}:${version}`

/**
 * Reads a version-matching crash-recovery mirror, if one exists and is not stale. Never throws. A mirror written
 * by an older client is hydrated like a server document, so it carries every field the reducer expects.
 */
export const readGtMirror = (meetingId: string, version: number): GtMirror | null => {
  try {
    const raw = sessionStorage.getItem(mirrorKey(meetingId, version))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { doc: LegacyGtDocument; at: number }
    if (Date.now() - parsed.at > MIRROR_MAX_AGE_MS) {
      sessionStorage.removeItem(mirrorKey(meetingId, version))
      return null
    }
    return { doc: hydrateDoc(parsed.doc), at: parsed.at }
  } catch {
    return null
  }
}

const writeGtMirror = (meetingId: string, version: number, doc: GtDoc): void => {
  try {
    sessionStorage.setItem(mirrorKey(meetingId, version), JSON.stringify({ doc, at: Date.now() }))
  } catch {
    // Best-effort only: a full or blocked session store must never block annotating.
  }
}

export const clearGtMirror = (meetingId: string, version: number): void => {
  try {
    sessionStorage.removeItem(mirrorKey(meetingId, version))
  } catch {
    // no-op
  }
}

const mirrorPrefix = (meetingId: string) => `gt:${meetingId}:`

/** Removes every mirror of this meeting, at any version. Never throws. */
export const clearGtMirrors = (meetingId: string): void => {
  try {
    const prefix = mirrorPrefix(meetingId)
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(prefix)) keys.push(key)
    }
    keys.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // no-op
  }
}

/** Scans sessionStorage for any non-stale mirror of this meeting, at any version. */
export const findGtMirror = (meetingId: string): { version: number; mirror: GtMirror } | null => {
  try {
    const prefix = mirrorPrefix(meetingId)
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (!key?.startsWith(prefix)) continue
      const version = Number(key.slice(prefix.length))
      if (!Number.isFinite(version)) continue
      const mirror = readGtMirror(meetingId, version)
      if (mirror) return { version, mirror }
    }
    return null
  } catch {
    return null
  }
}

export interface UseGtDocumentArgs {
  meetingId: string
  doc: GtDoc
  version: number
  dirty: boolean
  editSeq: number
  readOnly: boolean
  prefillVersion: string
  refinedRawSha256: string
  onAdopt: (version: number, sha: string, editSeq: number) => void
  onReloadTheirs?: () => void
}

export interface UseGtDocumentResult {
  status: GtSaveStatus
  errorReason: string | null
  conflict: GtConflictInfo | null
  flush: () => void
  /** Waits for any in-flight save, then saves pending edits; resolves with the settled server version. */
  settle: () => Promise<GtSettleResult>
  retry: () => void
  /** Stops autosave and drops every local mirror ahead of deleting the server draft; `doc` reloading resumes it. */
  discardLocal: () => void
  resolveConflictReload: () => void
  resolveConflictOverwrite: () => void
  resolveConflictCancel: () => void
}

export const useGtDocument = ({
  meetingId,
  doc,
  version,
  dirty,
  editSeq,
  readOnly,
  prefillVersion,
  refinedRawSha256,
  onAdopt,
  onReloadTheirs,
}: UseGtDocumentArgs): UseGtDocumentResult => {
  const { mutateAsync: saveGt } = useSaveAttributionGt(meetingId)
  const [phase, setPhase] = useState<'idle' | 'saving' | 'error'>('idle')
  const [errorReason, setErrorReason] = useState<string | null>(null)
  const [conflict, setConflict] = useState<GtConflictInfo | null>(null)

  const inFlightRef = useRef<{ promise: Promise<GtSaveOutcome>; editSeq: number } | null>(null)
  const suspendedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestRef = useRef({ doc, version, editSeq, dirty })
  latestRef.current = { doc, version, editSeq, dirty }
  const prevDocRef = useRef(doc)

  // Also bumps latestRef so a save chained before the next render targets the adopted version.
  const adopt = useCallback(
    (adoptedVersion: number, sha: string, targetEditSeq: number) => {
      onAdopt(adoptedVersion, sha, targetEditSeq)
      latestRef.current = { ...latestRef.current, version: adoptedVersion }
    },
    [onAdopt]
  )

  // Overwrite passes the local version the mirror was written under as mirrorVersion.
  const performSave = useCallback(
    async (
      targetVersion: number,
      targetDoc: GtDoc,
      targetEditSeq: number,
      mirrorVersion: number = targetVersion
    ): Promise<GtSaveOutcome> => {
      setPhase('saving')
      try {
        const response = await saveGt({
          expected_version: targetVersion,
          document: targetDoc,
          prefill_version: targetVersion === 0 ? prefillVersion : null,
          refined_raw_sha256: targetVersion === 0 ? refinedRawSha256 : null,
        })
        adopt(response.data.version, response.data.document_sha256, targetEditSeq)
        clearGtMirror(meetingId, targetVersion)
        clearGtMirror(meetingId, mirrorVersion)
        setPhase('idle')
        setErrorReason(null)
        setConflict(null)
        return 'saved'
      } catch (error: unknown) {
        const conflictBody = getGtConflict(error)
        if (conflictBody) {
          // No valid document to reconcile against: the transcript itself moved out from under us.
          if (conflictBody.reason === 'prefill_mismatch' || conflictBody.reason === 'source_unavailable') {
            suspendedRef.current = true
            setPhase('idle')
            setConflict({ currentVersion: conflictBody.current_version ?? null, reason: conflictBody.reason })
            return 'conflict'
          }
          // Finalized away by someone else: nothing to overwrite onto, only a reload makes sense.
          if (conflictBody.reason === 'stale_version' && conflictBody.current_version == null) {
            suspendedRef.current = true
            setPhase('idle')
            setConflict({ currentVersion: null, reason: conflictBody.reason })
            return 'conflict'
          }
          // Hashing must never throw; treat failure as an unverified conflict, not a crash.
          let oursSha: string | null = null
          try {
            oursSha = await canonicalJsonSha256(targetDoc)
          } catch {
            oursSha = null
          }
          if (oursSha !== null && conflictBody.document_sha256 === oursSha && conflictBody.current_version != null) {
            adopt(conflictBody.current_version, conflictBody.document_sha256, targetEditSeq)
            clearGtMirror(meetingId, targetVersion)
            clearGtMirror(meetingId, mirrorVersion)
            setPhase('idle')
            setConflict(null)
            return 'saved'
          }
          suspendedRef.current = true
          setPhase('idle')
          setConflict({ currentVersion: conflictBody.current_version ?? null, reason: conflictBody.reason })
          return 'conflict'
        }
        const refusal = getGtRefusal(error)
        if (refusal) {
          suspendedRef.current = true
          setPhase('error')
          setErrorReason(refusal.detail ?? refusal.reason)
          return 'error'
        }
        if (isApiError(error) && (error.status === 404 || error.status === 410)) {
          // Deleted/purged server-side; a stale mirror would only confuse a reload.
          clearGtMirror(meetingId, targetVersion)
          clearGtMirror(meetingId, mirrorVersion)
          suspendedRef.current = true
          setPhase('error')
          setErrorReason('gone')
          return 'error'
        }
        if (isApiError(error) && (error.status === 503 || error.status === 429)) {
          suspendedRef.current = true
          setPhase('error')
          setErrorReason(error.status === 503 ? 'Saves are temporarily disabled' : 'Too many saves, retry in a moment')
          return 'error'
        }
        // Unknown/unhandled status: suspend too, so only the explicit Retry button drives recovery.
        suspendedRef.current = true
        setPhase('error')
        setErrorReason(null)
        return 'error'
      }
    },
    [meetingId, adopt, saveGt, prefillVersion, refinedRawSha256]
  )

  const runSave = useCallback(
    (targetVersion: number, targetDoc: GtDoc, targetEditSeq: number, mirrorVersion?: number) => {
      if (inFlightRef.current) return inFlightRef.current.promise
      const promise = performSave(targetVersion, targetDoc, targetEditSeq, mirrorVersion).finally(() => {
        inFlightRef.current = null
      })
      inFlightRef.current = { promise, editSeq: targetEditSeq }
      return promise
    },
    [performSave]
  )

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const flush = useCallback(() => {
    if (readOnly || conflict || suspendedRef.current) return
    const { doc: targetDoc, version: targetVersion, editSeq: targetEditSeq } = latestRef.current
    clearTimer()
    void runSave(targetVersion, targetDoc, targetEditSeq)
  }, [runSave, clearTimer, readOnly, conflict])

  const settle = useCallback(async (): Promise<GtSettleResult> => {
    if (readOnly) return { outcome: 'error' }
    const inFlight = inFlightRef.current
    if (inFlight) {
      const outcome = await inFlight.promise
      if (outcome !== 'saved') return { outcome }
      if (latestRef.current.editSeq === inFlight.editSeq)
        return { outcome: 'saved', version: latestRef.current.version }
    } else if (!latestRef.current.dirty) {
      return { outcome: 'saved', version: latestRef.current.version }
    }
    if (conflict) return { outcome: 'conflict' }
    if (suspendedRef.current) return { outcome: 'error' }
    const { doc: targetDoc, version: targetVersion, editSeq: targetEditSeq } = latestRef.current
    clearTimer()
    const outcome = await runSave(targetVersion, targetDoc, targetEditSeq)
    return outcome === 'saved' ? { outcome, version: latestRef.current.version } : { outcome }
  }, [runSave, clearTimer, readOnly, conflict])

  useEffect(() => {
    if (readOnly) return
    if (doc !== prevDocRef.current) {
      suspendedRef.current = false
      prevDocRef.current = doc
    }
    if (!dirty) return
    if (suspendedRef.current || conflict) return
    writeGtMirror(meetingId, version, doc)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [doc, dirty, readOnly, meetingId, version, flush, conflict])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && dirty && !readOnly) flush()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [dirty, readOnly, flush])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty && !readOnly) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty, readOnly])

  const retry = useCallback(() => {
    suspendedRef.current = false
    flush()
  }, [flush])

  const resolveConflictCancel = useCallback(() => {
    setConflict(null)
    suspendedRef.current = false
  }, [])

  const discardLocal = useCallback(() => {
    setConflict(null)
    suspendedRef.current = true
    clearTimer()
    clearGtMirrors(meetingId)
  }, [meetingId, clearTimer])

  const resolveConflictReload = useCallback(() => {
    setConflict(null)
    // Stays suspended until `doc` reloads, so the debounce effect can't rewrite the cleared mirror.
    suspendedRef.current = true
    clearGtMirror(meetingId, latestRef.current.version)
    onReloadTheirs?.()
  }, [onReloadTheirs, meetingId])

  const resolveConflictOverwrite = useCallback(() => {
    // No valid target version to overwrite onto (row gone, or a mismatched prefill needs a reload instead).
    if (
      conflict?.currentVersion == null ||
      conflict.reason === 'prefill_mismatch' ||
      conflict.reason === 'source_unavailable'
    )
      return
    setConflict(null)
    suspendedRef.current = false
    const { doc: targetDoc, version: mirrorVersion, editSeq: targetEditSeq } = latestRef.current
    void runSave(conflict.currentVersion, targetDoc, targetEditSeq, mirrorVersion)
  }, [conflict, runSave])

  const status: GtSaveStatus = readOnly
    ? 'readonly'
    : conflict
      ? 'conflict'
      : phase === 'saving'
        ? 'saving'
        : phase === 'error'
          ? 'error'
          : dirty
            ? 'unsaved'
            : 'saved'

  return useMemo(
    () => ({
      status,
      errorReason,
      conflict,
      flush,
      settle,
      retry,
      discardLocal,
      resolveConflictReload,
      resolveConflictOverwrite,
      resolveConflictCancel,
    }),
    [
      status,
      errorReason,
      conflict,
      flush,
      settle,
      retry,
      discardLocal,
      resolveConflictReload,
      resolveConflictOverwrite,
      resolveConflictCancel,
    ]
  )
}
